/**
 * Issue #689 — the ground picker's "Saved grounds" group.
 *
 * `GroundPicker.jsx` merges three sources into one alphabetically-sorted
 * list (`suggestions`), which is how a player's own bookmarks ended up
 * buried: sorted alphabetically and cut to 8, a saved ground with a name
 * past the letter "H" or so never surfaces once the shared directory holds
 * more than a handful of rows — which it now does, world-wide.
 *
 * The fix mirrors `handleNearMe` (#134), which already proves the shape:
 * a titled group that stands apart from the alphabetical merge rather than
 * sorting into it. This module is the pure half of that — which rows belong
 * in the saved group and which are left for the general list — kept out of
 * JSX for the reason every other `lib/*.js` in this folder gives: this repo
 * mounts no component in a unit test, so a rule written only inside JSX can
 * only be grepped, and ordering/precedence is exactly the kind of rule a
 * later tidy reverts by accident.
 *
 * INPUT is `GroundPicker`'s own `suggestions` memo: already merged, already
 * deduped by ground id, already tagged with `source` — and a row is tagged
 * `"saved"` if and only if it is (or is also) one of the caller's bookmarks,
 * because the merge in `GroundPicker.jsx` writes the saved source LAST, so
 * it always wins the dedup regardless of what else the row was.
 *
 * PRECEDENCE. A saved row that matches the typed text is claimed by the
 * saved group — shown there (up to `cap`) or accounted for in its "Showing
 * N of M" note — and is never handed to the general list to reappear lower
 * down. That is requirement 2 in #689: filtering must not let a saved match
 * merge back into the alphabetical list it was pulled out of.
 */

/** How many saved grounds the group shows before it defers to the note. */
export const SAVED_GROUP_CAP = 5;

/** The same identity the merge in `GroundPicker.jsx` dedupes rows by. */
const suggestionKey = (s) => s.groundId || `name:${(s.name || "").trim().toLowerCase()}`;

const matchesTyped = (s, needle) => !needle || (s.name || "").toLowerCase().includes(needle);

/**
 * Split a merged suggestion list into the saved group and what is left for
 * the general list.
 *
 * `typed` is the raw field value — trimmed and lower-cased here, the same
 * way `GroundPicker`'s own filter does it, so the two halves agree on what
 * "matches" means. Empty (or all whitespace) means every saved row matches,
 * which is the empty-field state the issue asks for: the group shows the
 * player's saved grounds with nothing typed at all.
 *
 * Returns:
 *   - `saved` — up to `cap` matching saved rows, in the order they arrived
 *     (the caller's list is already alphabetical).
 *   - `savedMatchCount` — how many saved rows actually match, which may be
 *     more than `saved.length`; the difference is what the "Showing N of M"
 *     note reports.
 *   - `general` — every row NOT claimed by a saved match, unfiltered by
 *     `typed` (the caller still applies its own text filter and cap to
 *     this, exactly as it did to the unsplit list before).
 */
export function groupGroundSuggestions(suggestions, typed, { cap = SAVED_GROUP_CAP } = {}) {
  const list = Array.isArray(suggestions) ? suggestions : [];
  const needle = (typed || "").trim().toLowerCase();

  const savedMatches = list.filter((s) => s.source === "saved" && matchesTyped(s, needle));
  const claimed = new Set(savedMatches.map(suggestionKey));

  return {
    saved: savedMatches.slice(0, cap),
    savedMatchCount: savedMatches.length,
    general: list.filter((s) => !claimed.has(suggestionKey(s))),
  };
}
